import { classifyRouterError } from './classify-router-error';
import { hasReloadBudget, spendReloadBudget } from './reload-budget';

/** sessionStorage key recording the last stale-client auto-reload (epoch ms). */
const RELOAD_GUARD_KEY = 'archipro:stale-client-reload-at';

/** sessionStorage key counting stale-client auto-reloads spent in the current window. */
const RELOAD_BUDGET_KEY = 'archipro:stale-client-reload-count';

/**
 * Router-error categories (from `classifyRouterError`) that mean the client is
 * out of step with the server — almost always a new build shipping while the
 * tab sat open. A fresh page load picks up the new build and recovers:
 *  - `chunk load`          a JS chunk / dynamically imported module 404s
 *  - `turbo-stream decode` a single-fetch `.data` response the stale client
 *                          can't decode (also covers a session/edge HTML
 *                          response, where reloading lands the real redirect)
 *  - `routeId not found`   the single-fetch result is missing the requested
 *                          route, i.e. the client/server route trees differ
 *  - `css preload`         a route chunk's sibling CSS asset 404s under the
 *                          previous build's `/_build/<version>/` prefix
 */
const STALE_CLIENT_CATEGORIES = new Set([
    'chunk load',
    'turbo-stream decode',
    'routeId not found',
    'css preload',
]);

/** True for the deploy-race / stale-client errors a reload recovers from. */
export const isStaleClientError = (error: Error): boolean =>
    STALE_CLIENT_CATEGORIES.has(classifyRouterError(error));

/**
 * Cooldown after an auto-reload before another is allowed. Prevents reload loops
 * when a freshly deployed build is itself broken — a second failure inside this
 * window falls through to the error page instead of reloading again.
 */
export const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Pure loop-guard decision: given the last auto-reload time, may we reload now?
 * Extracted so the guard can be unit tested without touching the DOM.
 */
export const canAutoReload = (now: number, lastReloadAt: number): boolean =>
    now - lastReloadAt >= RELOAD_COOLDOWN_MS;

/**
 * Recover from the deploy-race that strands an open tab on the error page.
 *
 * When a new build ships while a tab is open, the next navigation's chunk or
 * single-fetch `.data` request hits a server the stale client can't agree with
 * — surfacing as `chunk load`, `turbo-stream decode`, `routeId not found`, or
 * `css preload` (the same deploy the `🎊 ... has been released` notification
 * announces).
 * Reloading once picks up the new build. Any other error class is left alone so
 * genuine failures still surface.
 *
 * Two sessionStorage guards, doing different jobs. The cooldown paces reloads,
 * so a build that is itself broken is not retried immediately. The windowed
 * budget (`reload-budget`) ends the loop outright, which the cooldown alone
 * cannot do: a permanently missing asset fails again on every later click, and
 * each of those is past the cooldown. Because the budget is windowed rather
 * than per-tab, a tab left open across several deploys still recovers from each
 * one. If sessionStorage is unavailable (private mode / sandboxed iframe)
 * neither guard works, so we decline to reload.
 *
 * @returns true if a reload was triggered — the caller should stop and skip
 *          error logging, since this is an expected, self-healed deploy race.
 */
export const tryReloadOnStaleClient = (error: Error): boolean => {
    if (typeof window === 'undefined') return false;
    if (!isStaleClientError(error)) return false;

    let lastReloadAt: number;
    try {
        lastReloadAt = Number(
            window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0,
        );
    } catch {
        return false;
    }

    const now = Date.now();
    if (!canAutoReload(now, lastReloadAt)) return false;
    // Checked before the cooldown timestamp is written and spent after, so
    // neither guard is charged for a reload the other one refuses: a spent
    // budget must not bump the cooldown, and a failed cooldown write must not
    // cost an attempt.
    if (!hasReloadBudget(RELOAD_BUDGET_KEY)) return false;

    try {
        window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
    } catch {
        return false;
    }

    if (!spendReloadBudget(RELOAD_BUDGET_KEY)) return false;

    console.warn(
        '[ErrorPage] stale client after deploy — reloading once to recover',
        error.message,
    );
    window.location.reload();
    return true;
};
